import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { DeviceServiceService } from 'src/app/modules/devices/device-service.service';
import { ServicecallService } from 'src/app/services';

export interface SelectableItem {
  _id: string;
  deviceName: string;
  [key: string]: any;
}

@Component({
  selector: 'app-selectable-popover',
  templateUrl: './selectable-popover.component.html',
  styleUrls: ['./selectable-popover.component.scss'],
})
export class SelectablePopoverComponent<
  T extends SelectableItem = SelectableItem
> implements OnInit, OnDestroy
{
  @Input() title: string = 'Select Item';
  @Input() searchPlaceholder: string = 'Search...';
  @Input() entityName: string;
  @Input() multiSelect: boolean = false;
  @Input() showSearchBar: boolean = true;
  @Input() labelKey: string = 'label';
  @Input() customItemTemplate?: TemplateRef<any>;
  @Input() maxHeight: string = '';

  @Output() cancel = new EventEmitter<void>();
  @Output() done = new EventEmitter<T | T[] | null>();
  @Output() selectionChange = new EventEmitter<T | T[] | null>();

  searchControl = new FormControl('');
  private _selectedItems: T[] = [];
  private _masterDeviceData: T[] = [];
  private _subscription = new Subscription();
  private _getDevicesSubscription: Subscription;

  get filteredItems(): T[] {
    const searchValue = this.searchControl.value?.toLowerCase() || '';

    if (!searchValue) {
      return this._masterDeviceData;
    }

    return this._masterDeviceData?.filter((item) =>
      item[`deviceName`].toString().toLowerCase().includes(searchValue)
    );
  }

  get hasSelection(): boolean {
    return this._selectedItems.length > 0;
  }
  constructor(
    private _sharedService: ServicecallService,
    private _service: DeviceServiceService
  ) {}
  ngOnInit(): void {
    // Add debounce to search for better performance
    this._subscription.add(
      this.searchControl.valueChanges
        .pipe(debounceTime(300), distinctUntilChanged())
        .subscribe(() => {
          // Search logic is handled in filteredItems getter
        })
    );
    this._getDeviceList();
  }

  ngOnDestroy(): void {
    this._subscription?.unsubscribe();
    this._getDevicesSubscription?.unsubscribe();
  }

  private _getDeviceList(): void {
    const devicePayload = {
      page: 0,
      siteId: this._sharedService.getSiteId(),
      deviceType: null,
      deviceName: null,
    };

    this._getDevicesSubscription?.unsubscribe();
    this._getDevicesSubscription = this._service
      .getDevices(devicePayload)
      .subscribe({
        next: (res: any) => {
          const data = res?.response?.data || [];
          this._masterDeviceData = res?.status
            ? this._filterItemsWithoutEntityValues(data)
            : [];
          if (!res?.status) {
            this._sharedService.toasterService({
              type: 'error',
              msg: res?.response?.message || 'Failed to fetch devices',
            });
          }
        },
        error: (err) => {
          this._masterDeviceData = [];
          this._sharedService.toasterService({
            type: 'error',
            msg: 'Error fetching device list',
          });
          console.error('Device list fetch error:', err);
        },
      });
  }

  /**
   * Filters an array to exclude objects that have entities with values in their mapping
   * @param {Array} dataArray - The array to filter
   * @returns {Array} - Filtered array excluding objects with non-empty entities
   */
  private _filterItemsWithoutEntityValues(dataArray) {
    if (!Array.isArray(dataArray)) {
      return [];
    }

    return dataArray.filter((item) => {
      // If mapping doesn't exist or is empty, include the item
      if (!Array.isArray(item.mapping) || item.mapping.length === 0) {
        return true;
      }

      // Check if all mapping entries either don't have entities or have empty entities
      return item.mapping.every(
        (mapItem) =>
          !mapItem.entities ||
          typeof mapItem.entities !== 'object' ||
          Object.keys(mapItem.entities).length === 0
      );
    });
  }

  toggleSelection(item: T): void {
    const index = this._selectedItems.findIndex(
      (selected) => selected?._id === item?._id
    );

    if (index > -1) {
      this._selectedItems.splice(index, 1);
    } else {
      if (!this.multiSelect) {
        this._selectedItems = [];
      }
      this._selectedItems.push(item);
    }

    this.emitSelectionChange();
  }

  isSelected(item: T): boolean {
    return this._selectedItems.some((selected) => selected?._id === item?._id);
  }

  isDisabled(item: T): boolean {
    return (
      !this.multiSelect &&
      this._selectedItems.length > 0 &&
      !this.isSelected(item)
    );
  }

  emitSelectionChange(): void {
    if (this.multiSelect) {
      this.selectionChange.emit([...this._selectedItems]);
    } else {
      this.selectionChange.emit(this._selectedItems[0] || null);
    }
  }

  onCancel(): void {
    this.cancel.emit();
  }

  onDone(): void {
    if (this.multiSelect) {
      this.done.emit([...(this._selectedItems ?? [])]);
    } else {
      this.done.emit(this._selectedItems?.[0] ?? null);
    }
  }

  trackById(index: number, item: SelectableItem): string {
    return item?._id;
  }
}
